import { apiClient } from '@/api/axios';
import { endpoints } from '@/api/endpoints';
import {
  permissionActionOrder,
  resourceRegistry,
} from '@/authorization/ResourceRegistry';
import type {
  Permission,
  PermissionAction,
  PermissionResourceKey,
  Role,
} from '@/authorization/types';

const mockDelayMs = 180;

function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), mockDelayMs);
  });
}

function permissionId(
  resource: PermissionResourceKey,
  action: PermissionAction,
) {
  return `${resource}:${action}`;
}

function buildRolePermissions(
  allowRules: Partial<Record<PermissionResourceKey, PermissionAction[]>>,
): Permission[] {
  return resourceRegistry.flatMap((resource) =>
    resource.supportedActions.map((action) => ({
      id: permissionId(resource.key, action),
      resource: resource.key,
      action,
      allowed: (allowRules[resource.key] ?? []).includes(action),
      inherited: false,
      scope: 'global' as const,
      featureFlag: resource.featureFlag,
    })),
  );
}

const roleMockData: Role[] = [
  {
    id: 'role-global-admin',
    name: 'Global Admin',
    description:
      'Unrestricted write access across entire global hierarchy database and nodes.',
    color: '#dc2626',
    priority: 1,
    isSystemRole: true,
    assignedUsers: 3,
    permissions: buildRolePermissions(
      Object.fromEntries(
        resourceRegistry.map((resource) => [
          resource.key,
          [...permissionActionOrder],
        ]),
      ) as Partial<Record<PermissionResourceKey, PermissionAction[]>>,
    ),
    createdDate: '2025-01-08T10:00:00.000Z',
    lastModified: '2026-07-20T14:00:00.000Z',
  },
  {
    id: 'role-product-admin',
    name: 'Product Admin',
    description:
      'Full permissions on product config, entity mappings and menus.',
    color: '#f59e0b',
    priority: 2,
    isSystemRole: false,
    assignedUsers: 12,
    permissions: buildRolePermissions({
      products: ['read', 'create', 'update', 'delete', 'admin'],
      productMenuConfiguration: ['read', 'create', 'update', 'delete', 'admin'],
      embeddedApplications: ['read', 'update'],
      notifications: ['read', 'update'],
      users: ['read'],
      groups: ['read'],
      roles: ['read'],
      auditLogs: ['read'],
      featureFlags: ['read', 'update'],
      systemSettings: ['read'],
      apiConfiguration: ['read'],
      documentationHub: ['read', 'update'],
    }),
    createdDate: '2025-01-19T10:00:00.000Z',
    lastModified: '2026-07-20T14:00:00.000Z',
  },
  {
    id: 'role-editor',
    name: 'Editor',
    description:
      'Can edit mappings, profiles and read all core database system attributes.',
    color: '#2563eb',
    priority: 3,
    isSystemRole: false,
    assignedUsers: 45,
    permissions: buildRolePermissions({
      products: ['read', 'update'],
      productMenuConfiguration: ['read', 'update'],
      users: ['read'],
      groups: ['read'],
      roles: ['read'],
      notifications: ['read', 'update'],
      embeddedApplications: ['read', 'update'],
      documentationHub: ['read', 'update'],
      auditLogs: ['read'],
      featureFlags: ['read'],
      systemSettings: ['read'],
      apiConfiguration: ['read'],
    }),
    createdDate: '2025-02-01T10:00:00.000Z',
    lastModified: '2026-07-20T14:00:00.000Z',
  },
  {
    id: 'role-viewer',
    name: 'Viewer',
    description:
      'General portal access with static view privileges and no mutation access.',
    color: '#64748b',
    priority: 4,
    isSystemRole: false,
    assignedUsers: 120,
    permissions: buildRolePermissions({
      products: ['read'],
      productMenuConfiguration: ['read'],
      users: ['read'],
      groups: ['read'],
      roles: ['read'],
      auditLogs: ['read'],
      featureFlags: ['read'],
      systemSettings: ['read'],
      apiConfiguration: ['read'],
      notifications: ['read'],
      embeddedApplications: ['read'],
      documentationHub: ['read'],
    }),
    createdDate: '2025-03-01T10:00:00.000Z',
    lastModified: '2026-07-20T14:00:00.000Z',
  },
];

export const authorizationService = {
  getRoles: async (): Promise<Role[]> => {
    try {
      const response = await apiClient.get<Role[]>(endpoints.roles);
      return response.data;
    } catch {
      return delay(roleMockData);
    }
  },

  getRoleById: async (roleId: string): Promise<Role> => {
    try {
      const response = await apiClient.get<Role>(endpoints.roleById(roleId));
      return response.data;
    } catch {
      const role = roleMockData.find((item) => item.id === roleId);

      if (!role) {
        throw new Error('Role not found.');
      }

      return delay(role);
    }
  },

  createRole: async (payload: Partial<Role>): Promise<Role> => {
    try {
      const response = await apiClient.post<Role>(endpoints.roles, payload);
      return response.data;
    } catch {
      return delay({
        id: payload.id ?? `role-${Date.now()}`,
        name: payload.name ?? '',
        description: payload.description ?? '',
        color: payload.color ?? '#2563eb',
        priority: payload.priority ?? 99,
        isSystemRole: false,
        assignedUsers: 0,
        permissions: payload.permissions ?? [],
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
      });
    }
  },

  updateRole: async (roleId: string, payload: Partial<Role>): Promise<Role> => {
    try {
      const response = await apiClient.put<Role>(
        endpoints.roleById(roleId),
        payload,
      );
      return response.data;
    } catch {
      const current = roleMockData.find((item) => item.id === roleId);

      if (!current) {
        throw new Error('Role not found.');
      }

      return delay({
        ...current,
        ...payload,
        lastModified: new Date().toISOString(),
      });
    }
  },

  deleteRole: async (roleId: string): Promise<void> => {
    try {
      await apiClient.delete(endpoints.roleById(roleId));
    } catch {
      void roleId;
      await delay(undefined);
    }
  },

  getPermissions: async (): Promise<Permission[]> => {
    try {
      const response = await apiClient.get<Permission[]>(endpoints.permissions);
      return response.data;
    } catch {
      return delay(
        resourceRegistry.flatMap((resource) =>
          resource.supportedActions.map((action) => ({
            id: permissionId(resource.key, action),
            resource: resource.key,
            action,
            allowed: false,
            inherited: false,
            scope: 'global' as const,
            featureFlag: resource.featureFlag,
          })),
        ),
      );
    }
  },

  updateRolePermissions: async (
    roleId: string,
    permissions: Permission[],
  ): Promise<void> => {
    try {
      await apiClient.put(endpoints.rolePermissions(roleId), { permissions });
    } catch {
      await delay(undefined);
    }
  },

  cloneRole: async (
    sourceRoleId: string,
    targetName: string,
  ): Promise<Role> => {
    try {
      const response = await apiClient.post<Role>(endpoints.rolesClone, {
        sourceRoleId,
        targetName,
      });
      return response.data;
    } catch {
      const sourceRole = roleMockData.find((item) => item.id === sourceRoleId);

      if (!sourceRole) {
        throw new Error('Source role not found.');
      }

      return delay({
        ...sourceRole,
        id: `role-${Date.now()}`,
        name: targetName,
        isSystemRole: false,
        assignedUsers: 0,
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
      });
    }
  },
};
